import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DayPageService, PageDTO } from './api/day-page.service';
import { DayBlockService } from './api/day-block.service';
import {
  DayBlockDTO,
  IDayBlockDTO,
} from '@app/infrastructure/models/day-block';

@Injectable({
  providedIn: 'root',
})
export class DayPageFacade {
  constructor(private dayPageService: DayPageService) {}

  private pages$: BehaviorSubject<PageDTO[]> = new BehaviorSubject([]);
  private activePage$: BehaviorSubject<PageDTO> = new BehaviorSubject(null);
  private blocks$: BehaviorSubject<IDayBlockDTO[]> = new BehaviorSubject([]);

  public get activePage(): PageDTO {
    return this.activePage$.getValue();
  }

  public set activePage(page: PageDTO) {
    this.activePage$.next(page);
  }

  public get blocks(): IDayBlockDTO[] {
    return this.blocks$.getValue();
  }

  public get blocksObs$(): Observable<IDayBlockDTO[]> {
    return this.blocks$.asObservable();
  }

  public set blocks(blocks: IDayBlockDTO[]) {
    this.blocks$.next(blocks);
  }

  public get pages(): PageDTO[] {
    return this.pages$.getValue();
  }

  public set pages(pages: PageDTO[]) {
    this.pages$.next(pages);
  }

  public get pagesObs$(): Observable<PageDTO[]> {
    return this.pages$.asObservable();
  }

  public get nextPage(): PageDTO {
    const index = this.pages.findIndex(
      (page) => page.id === this.activePage.id,
    );
    if (index < this.pages.length - 1) {
      return this.pages[index + 1];
    }
    return null;
  }

  public get prevPage(): PageDTO {
    const index = this.pages.findIndex(
      (page) => page.id === this.activePage.id,
    );
    if (index > 0) {
      return this.pages[index - 1];
    }
    return null;
  }

  public goToNextPage() {
    const index = this.pages.findIndex(
      (page) => page.id === this.activePage.id,
    );
    if (index < this.pages.length - 1) {
      const nextPage = this.pages[index + 1];
      this.activePage = nextPage;
      this.updateBlocks();
    }
  }

  public goToPreviousPage() {
    const index = this.pages.findIndex(
      (page) => page.id === this.activePage.id,
    );
    if (index > 0) {
      const prevPage = this.pages[index - 1];
      this.activePage = prevPage;
      this.updateBlocks();
    }
  }

  public clear() {
    this.pages$.next([]);
    this.activePage$.next(null);
    this.blocks$.next([]);
  }

  public getCurrentPage(): number {
    return this.pages.findIndex((page) => page.id === this.activePage.id) + 1;
  }

  public isFirstPage(): boolean {
    return this.pages.findIndex((page) => page.id === this.activePage.id) === 0;
  }

  public isLastPage(): boolean {
    return (
      this.pages.findIndex((page) => page.id === this.activePage.id) ===
      this.pages.length - 1
    );
  }

  public initialize(gradeUnitDayId: number, pageNumber: string = null) {
    this.dayPageService.getDayPages(gradeUnitDayId).subscribe((pages) => {
      this.pages = pages;
      if (pages && pages.length > 0) {
        if (pageNumber) {
          const page = pages.find((page) => page.pageNumber === pageNumber);
          if (page) {
            this.activePage = page;
          }
        } else {
          this.activePage = pages[0];
        }
        this.updateBlocks();
      }
    });
  }

  public setPage(pageNumber: string) {
    const page = this.pages.find((page) => page.pageNumber === pageNumber);
    if (page) {
      this.activePage = page;
      this.updateBlocks();
    }
  }

  public updateBlocks() {
    this.blocks = this.activePage?.blocks;
  }
}
